import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions, WithPermission } from '../contexts/PermissionsContext';

// Import tab components
import AuditTab from '../components/admin/AuditTab';
import ApiKeysTab from '../components/admin/ApiKeysTab';
import ScheduleTab from '../components/admin/ScheduleTab';
import ProductionLinesTab from '../components/admin/ProductionLinesTab';
import InvoiceProcessingTab from '../components/admin/InvoiceProcessingTab';
import PerformanceMonitorTab from '../components/admin/PerformanceMonitorTab';
import { UserManagement } from '../components/UserManagement';
import { RoleManagement } from '../components/RoleManagement';

const Admin: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { loading: permissionsLoading, hasAnyPermission, hasPermission } = usePermissions();

  // --- Routing / Tabs ---
  const getTabFromPath = (
    pathname: string
  ):
    | 'users'
    | 'roles'
    | 'audit'
    | 'schedule'
    | 'production-lines'
    | 'invoice-processing'
    | 'api-keys'
    | 'performance' => {
    if (pathname.includes('/admin/api-keys')) return 'api-keys';
    if (pathname.includes('/admin/users')) return 'users';
    if (pathname.includes('/admin/roles')) {
      // Only allow roles tab for Super Admins
      if (!user || user.role_id !== 1) {
        return 'users';
      }
      return 'roles';
    }
    if (pathname.includes('/admin/audit')) return 'audit';
    if (pathname.includes('/admin/schedule')) return 'schedule';
    if (pathname.includes('/admin/production-lines')) {
      // Allow production-lines tab for Super Admin or users with admin.production_lines permission
      if (!user || (user.role_id !== 1 && !hasPermission('admin.production_lines'))) {
        return 'users';
      }
      return 'production-lines';
    }
    if (pathname.includes('/admin/invoice-processing')) {
      // Only allow invoice-processing tab for Super Admins
      if (!user || user.role_id !== 1) {
        return 'users';
      }
      return 'invoice-processing';
    }
    if (pathname.includes('/admin/performance')) {
      // Only allow performance tab for Super Admins
      if (!user || user.role_id !== 1) {
        return 'users';
      }
      return 'performance';
    }
    return 'users'; // default
  };

  const [activeTab, setActiveTab] = useState<
    | 'api-keys'
    | 'users'
    | 'roles'
    | 'audit'
    | 'schedule'
    | 'production-lines'
    | 'invoice-processing'
    | 'performance'
  >(getTabFromPath(location.pathname));

  useEffect(() => {
    const tab = getTabFromPath(location.pathname);
    setActiveTab(tab);
    // Redirect Super Admin-only tabs if not Super Admin
    const superAdminOnlyTabs = ['/admin/roles', '/admin/invoice-processing', '/admin/performance'];
    if (superAdminOnlyTabs.some((path) => location.pathname.includes(path))) {
      if (!user || user.role_id !== 1) {
        navigate('/admin/users');
      }
    }
    // Production Lines: allow Super Admin or admin.production_lines permission
    if (location.pathname.includes('/admin/production-lines')) {
      if (!user || (user.role_id !== 1 && !hasPermission('admin.production_lines'))) {
        navigate('/admin/users');
      }
    }
  }, [location.pathname, user, navigate, hasPermission]);

  const handleTabChange = (
    tab:
      | 'api-keys'
      | 'users'
      | 'roles'
      | 'audit'
      | 'schedule'
      | 'production-lines'
      | 'invoice-processing'
      | 'performance'
  ) => {
    setActiveTab(tab);
    navigate(`/admin/${tab}`);
  };

  // --- Shared ---
  const [error, setError] = useState('');

  // Wait for auth to load first
  if (!user) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Loading...</h1>
        </div>
      </div>
    );
  }

  // Check if user is Super Admin (role_id = 1) - only override
  const isSuperAdmin = user.role_id === 1;

  // Wait for permissions to load
  if (permissionsLoading) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Loading...</h1>
        </div>
      </div>
    );
  }

  // Allow access if Super Admin OR has any admin permission
  const hasAnyAdminPermission =
    isSuperAdmin ||
    hasAnyPermission([
      'admin.user_management',
      'admin.audit_logs',
      'admin.production_lines',
      'admin.production_schedule',
      'admin.invoice_processing',
    ]);

  if (!hasAnyAdminPermission) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Access Denied</h1>
        </div>
        <div className="card">
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // --- View ---
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Admin Panel</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Tabs */}
      <div style={{ marginBottom: '2rem' }}>
        <div
          style={{
            display: 'flex',
            borderBottom: '2px solid #e1e5e9',
            gap: 0,
          }}
        >
          <WithPermission permission="admin.user_management">
            <button
              className={`btn ${activeTab === 'api-keys' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => handleTabChange('api-keys')}
            >
              API Keys
            </button>
          </WithPermission>
          <WithPermission permission="admin.user_management">
            <button
              onClick={() => handleTabChange('users')}
              style={{
                padding: '1rem 1.5rem',
                border: 'none',
                backgroundColor: activeTab === 'users' ? '#007bff' : 'transparent',
                color: activeTab === 'users' ? 'white' : '#495057',
                borderBottom: activeTab === 'users' ? '2px solid #007bff' : '2px solid transparent',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '1rem',
                transition: 'all 0.2s ease',
              }}
            >
              User Management
            </button>
          </WithPermission>

          {isSuperAdmin && (
            <button
              onClick={() => handleTabChange('roles')}
              style={{
                padding: '1rem 1.5rem',
                border: 'none',
                backgroundColor: activeTab === 'roles' ? '#007bff' : 'transparent',
                color: activeTab === 'roles' ? 'white' : '#495057',
                borderBottom: activeTab === 'roles' ? '2px solid #007bff' : '2px solid transparent',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '1rem',
                transition: 'all 0.2s ease',
              }}
            >
              Role Management
            </button>
          )}

          <WithPermission permission="admin.audit_logs">
            <button
              onClick={() => handleTabChange('audit')}
              style={{
                padding: '1rem 1.5rem',
                border: 'none',
                backgroundColor: activeTab === 'audit' ? '#007bff' : 'transparent',
                color: activeTab === 'audit' ? 'white' : '#495057',
                borderBottom: activeTab === 'audit' ? '2px solid #007bff' : '2px solid transparent',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '1rem',
                transition: 'all 0.2s ease',
              }}
            >
              Audit Logs
            </button>
          </WithPermission>

          <WithPermission permission="admin.production_schedule">
            <button
              onClick={() => handleTabChange('schedule')}
              style={{
                padding: '1rem 1.5rem',
                border: 'none',
                backgroundColor: activeTab === 'schedule' ? '#007bff' : 'transparent',
                color: activeTab === 'schedule' ? 'white' : '#495057',
                borderBottom:
                  activeTab === 'schedule' ? '2px solid #007bff' : '2px solid transparent',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '1rem',
                transition: 'all 0.2s ease',
              }}
            >
              Production Schedule
            </button>
          </WithPermission>

          <WithPermission permission="admin.production_lines">
            <button
              onClick={() => handleTabChange('production-lines')}
              style={{
                padding: '1rem 1.5rem',
                border: 'none',
                backgroundColor: activeTab === 'production-lines' ? '#007bff' : 'transparent',
                color: activeTab === 'production-lines' ? 'white' : '#495057',
                borderBottom:
                  activeTab === 'production-lines' ? '2px solid #007bff' : '2px solid transparent',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '1rem',
                transition: 'all 0.2s ease',
              }}
            >
              Production Lines
            </button>
          </WithPermission>

          {isSuperAdmin && (
            <button
              onClick={() => handleTabChange('invoice-processing')}
              style={{
                padding: '1rem 1.5rem',
                border: 'none',
                backgroundColor: activeTab === 'invoice-processing' ? '#007bff' : 'transparent',
                color: activeTab === 'invoice-processing' ? 'white' : '#495057',
                borderBottom:
                  activeTab === 'invoice-processing'
                    ? '2px solid #007bff'
                    : '2px solid transparent',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '1rem',
                transition: 'all 0.2s ease',
              }}
            >
              Invoice Processing
            </button>
          )}

          {isSuperAdmin && (
            <button
              onClick={() => handleTabChange('performance')}
              style={{
                padding: '1rem 1.5rem',
                border: 'none',
                backgroundColor: activeTab === 'performance' ? '#007bff' : 'transparent',
                color: activeTab === 'performance' ? 'white' : '#28a745',
                borderBottom:
                  activeTab === 'performance' ? '2px solid #007bff' : '2px solid transparent',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '1rem',
                transition: 'all 0.2s ease',
              }}
            >
              📊 Performance Monitor
            </button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'api-keys' && hasPermission('admin.user_management') && <ApiKeysTab />}
      {activeTab === 'users' && <UserManagement onUserUpdate={() => {}} />}
      {activeTab === 'roles' && isSuperAdmin && <RoleManagement onRoleUpdate={() => {}} />}
      {activeTab === 'audit' && <AuditTab onError={setError} />}
      {activeTab === 'schedule' && <ScheduleTab onError={setError} />}
      {activeTab === 'production-lines' && hasPermission('admin.production_lines') && (
        <ProductionLinesTab onError={setError} />
      )}
      {activeTab === 'invoice-processing' && isSuperAdmin && <InvoiceProcessingTab />}
      {activeTab === 'performance' && isSuperAdmin && <PerformanceMonitorTab onError={setError} />}
    </div>
  );
};

export default Admin;
